import { createClient } from '@supabase/supabase-js';
import config from './config';

export const STORAGE_KEY = 'unia-auth-token';

const supabaseUrl = config.SUPABASE_URL;
const supabaseAnonKey = config.SUPABASE_ANON_KEY;
const mainDomain = config.MAIN_DOMAIN;
const isLocalDev = !supabaseUrl?.includes(mainDomain);

if (!supabaseUrl || !supabaseAnonKey) {
  // Fail loud in console — better than silent createClient throw.
  // Runtime config is read from window.__UNIA_CONFIG__ in /config.js.
  // eslint-disable-next-line no-console
  console.error(
    '[supabase] Missing SUPABASE_URL or SUPABASE_ANON_KEY. ' +
    'Check /config.js (window.__UNIA_CONFIG__) or VITE_* build args.',
    { supabaseUrl, hasKey: Boolean(supabaseAnonKey), mainDomain },
  );
}

// Hybrid storage: localStorage for full session (no 4KB cookie limit),
// but also mirror access_token into a small cookie for non-Supabase API calls
// (GiperTASK/GiperNote) that read `unia-auth-token` cookie directly.
export const hybridStorage = {
  getItem: (key: string): string | null => {
    if (typeof localStorage === 'undefined') return null;
    return localStorage.getItem(key);
  },
  setItem: (key: string, value: string): void => {
    if (typeof localStorage === 'undefined') return;
    localStorage.setItem(key, value);
    if (key === STORAGE_KEY && typeof document !== 'undefined') {
      try {
        const session = JSON.parse(value);
        const token = session?.access_token;
        if (token) {
          const domain = isLocalDev ? 'localhost' : `.${mainDomain}`;
          const secure = isLocalDev ? '' : 'Secure; ';
          document.cookie = `${encodeURIComponent(key)}=${encodeURIComponent(token)}; domain=${domain}; path=/; SameSite=Lax; ${secure}max-age=3600`;
        }
      } catch {}
    }
  },
  removeItem: (key: string): void => {
    if (typeof localStorage !== 'undefined') localStorage.removeItem(key);
    if (typeof document !== 'undefined' && key === STORAGE_KEY) {
      const domain = isLocalDev ? 'localhost' : `.${mainDomain}`;
      document.cookie = `${encodeURIComponent(key)}=; domain=${domain}; path=/; expires=Thu, 01 Jan 1970 00:00:01 GMT; SameSite=Lax`;
    }
  },
};

export const supabase = createClient(supabaseUrl || 'https://api.unia.one', supabaseAnonKey || '', {
  auth: {
    persistSession: true,
    storageKey: STORAGE_KEY,
    storage: hybridStorage,
    autoRefreshToken: true,
  },
});
